import { Globe } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../api/services";
import { switchLanguagePath } from "../../i18n/routing";

// Estrutura do footer. As cadeas resólvense con t() para que cambien
// dinamicamente cando o usuario alterna idioma. As href son fixas.
// A primeira columna (help) ten estrutura especial — ver render abaixo.
const FOOTER_COLUMNS = [
  {
    headingKey: "footer.columns.community.heading",
    links: [
      { labelKey: "footer.columns.community.links.depopulation", href: "/despoboacion" },
      { labelKey: "footer.columns.community.links.subsidies", href: "/axudas" },
      { labelKey: "footer.columns.community.links.billing", href: "/facturacion" },
      { labelKey: "footer.columns.community.links.fiscal", href: "/fiscalidade" },
    ],
  },
  {
    headingKey: "footer.columns.owner.heading",
    links: [
      { labelKey: "footer.columns.owner.links.publishMachine", href: "/propietario/maquinaria" },
      { labelKey: "footer.columns.owner.links.publishLand", href: "/propietario/terra" },
      { labelKey: "footer.columns.owner.links.protection", href: "/propietario/proteccion" },
      { labelKey: "footer.columns.owner.links.simulator", href: "/simulador" },
    ],
  },
  {
    headingKey: "footer.columns.leyro.heading",
    links: [
      { labelKey: "footer.columns.leyro.links.pricing", href: "/plans" },
      { labelKey: "footer.columns.leyro.links.b2b", href: "/b2b" },
      { labelKey: "footer.columns.leyro.links.terms", href: "/termos" },
      { labelKey: "footer.columns.leyro.links.privacy", href: "/privacidade" },
      { labelKey: "footer.columns.leyro.links.compliance", href: "/cumprimento" },
    ],
  },
];

export default function Footer() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Toggle simple gl ↔ es. Cando engadamos máis idiomas, abrir un menú.
  // Mesma lóxica de persistencia que o selector do header (cookie + perfil).
  async function toggleLanguage() {
    const current = (i18n.language || "gl").split("-")[0];
    const next = current === "gl" ? "es" : "gl";
    await i18n.changeLanguage(next);
    // Tamén actualiza a URL para reflectir o prefixo do idioma activo.
    const newPath = switchLanguagePath(
      location.pathname + location.search + location.hash,
      next
    );
    if (newPath !== location.pathname + location.search + location.hash) {
      navigate(newPath, { replace: true });
    }
    if (user) {
      try {
        await authService.updateLanguage(next);
      } catch {
        // Falla silenciosa — o cambio xa funciona localmente.
      }
    }
  }

  return (
    <footer className="border-t border-border bg-white">
      {/* Top section — columns */}
      <div className="max-w-[1760px] mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Columna 1 — Axuda + Contacto fusionado nunha soa columna */}
          <div>
            <h3 className="text-sm font-semibold text-dark mb-4">
              {t("footer.columns.help.heading")}
            </h3>
            <ul className="space-y-3 mb-6">
              <li>
                <Link to="/axuda" className="text-sm text-dark hover:underline cursor-pointer">
                  {t("footer.columns.help.links.support")}
                </Link>
              </li>
              <li>
                <Link to="/seguridade" className="text-sm text-dark hover:underline cursor-pointer">
                  {t("footer.columns.help.links.safety")}
                </Link>
              </li>
              <li>
                <Link to="/cancelacion" className="text-sm text-dark hover:underline cursor-pointer">
                  {t("footer.columns.help.links.cancellation")}
                </Link>
              </li>
            </ul>

            <h3 className="text-sm font-semibold text-dark mb-4">
              {t("footer.columns.contact.heading", { defaultValue: "Contacto" })}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/contacto" className="text-sm text-dark hover:underline cursor-pointer">
                  {t("footer.columns.contact.form", { defaultValue: "Formulario de contacto" })}
                </Link>
              </li>
            </ul>
          </div>

          {/* Columnas 2-4 — render xenérico */}
          {FOOTER_COLUMNS.map((col) => (
            <div key={col.headingKey}>
              <h3 className="text-sm font-semibold text-dark mb-4">
                {t(col.headingKey)}
              </h3>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.labelKey}>
                    <Link
                      to={link.href}
                      className="text-sm text-dark hover:underline cursor-pointer"
                    >
                      {t(link.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border" />

      {/* Bottom row */}
      <div className="max-w-[1760px] mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Left — copyright + legal links */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-dark">
          <span>{t("footer.copyright")}</span>
          <span className="text-muted hidden sm:inline">·</span>
          <Link to="/privacidade" className="hover:underline">
            {t("footer.bottomLinks.privacy")}
          </Link>
          <span className="text-muted">·</span>
          <Link to="/termos" className="hover:underline">
            {t("footer.bottomLinks.terms")}
          </Link>
          <span className="text-muted">·</span>
          <Link to="/sitemap" className="hover:underline">
            {t("footer.bottomLinks.sitemap")}
          </Link>
        </div>

        {/* Right — Language selector */}
        <button
          type="button"
          onClick={toggleLanguage}
          className="flex items-center gap-2 text-sm font-medium text-dark hover:underline cursor-pointer"
          aria-label={t("language.ariaSelect")}
        >
          <Globe size={16} aria-hidden="true" />
          <span>{t("language.currentBadge")}</span>
        </button>
      </div>
    </footer>
  );
}
